import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Upload, Edit2, Trash2, FileImage, Video, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MenuEditor from '@/components/MenuEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Content() {
  const [contents, setContents] = useState([]);
  const [screens, setScreens] = useState([]);
  const [open, setOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    type: 'image',
    file_url: '',
    duration: 10,
    target_screens: []
  });

  useEffect(() => {
    fetchContents();
    fetchScreens();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API_URL}/content`);
      setContents(response.data);
    } catch (error) {
      toast.error('Nie udało się pobrać treści');
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`);
      setScreens(response.data);
    } catch (error) {
      console.error('Failed to fetch screens');
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) {
      toast.error('Wybierz plik');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API_URL}/content/upload`, formDataUpload);
      
      setFormData(prev => ({ ...prev, file_url: response.data.file_url }));
      toast.success('Plik wysłany');
    } catch (error) {
      toast.error('Błąd podczas wysyłania pliku');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingContent) {
        await axios.put(`${API_URL}/content/${editingContent.id}`, formData);
        toast.success('Treść zaktualizowana');
      } else {
        await axios.post(`${API_URL}/content`, formData);
        toast.success('Treść dodana');
      }
      
      setOpen(false);
      resetForm();
      fetchContents();
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę treść?')) return;
    
    try {
      await axios.delete(`${API_URL}/content/${id}`);
      toast.success('Treść usunięta');
      fetchContents();
    } catch (error) {
      toast.error('Nie udało się usunąć treści');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'image',
      file_url: '',
      duration: 10,
      target_screens: []
    });
    setEditingContent(null);
  };

  const openEdit = (content) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      type: content.type,
      file_url: content.file_url || '',
      duration: content.duration,
      target_screens: content.target_screens || []
    });
    setOpen(true);
  };

  const handleSaveFromEditor = async (editorData) => {
    try {
      const contentData = {
        title: 'Menu - ' + new Date().toLocaleString('pl-PL'),
        type: 'menu',
        editor_data: editorData,
        duration: 30,
        target_screens: screens.filter(s => s.type === 'connected').map(s => s.id)
      };
      
      await axios.post(`${API_URL}/content`, contentData);
      toast.success('Menu zapisane');
      setEditorOpen(false);
      fetchContents();
    } catch (error) {
      toast.error('Błąd podczas zapisywania menu');
    }
  };

  const getContentTypeLabel = (type) => {
    const labels = {
      menu: 'Menu',
      ad_single: 'Obraz',
      ad_combined: 'Reklama łączona',
      image: 'Obraz',
      video: 'Wideo'
    };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div data-testid="content-container" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Treści</h1>
            <p className="text-zinc-400">Zarządzaj treściami wyświetlanymi na ekranach</p>
          </div>
          <div className="flex space-x-3">
            <Button
              data-testid="create-menu-button"
              onClick={() => setEditorOpen(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Stwórz menu
            </Button>
            <Button
              data-testid="upload-content-button"
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
              className="btn-primary"
            >
              <Upload className="w-4 h-4 mr-2" />
              Wgraj treść
            </Button>
          </div>
        </div>

        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edytor Menu</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Zaprojektuj menu dla ekranów połączonych (1080x1920px)
              </DialogDescription>
            </DialogHeader>
            <MenuEditor onSave={handleSaveFromEditor} onCancel={() => setEditorOpen(false)} />
          </DialogContent>
        </Dialog>

        <Dialog open={open} onOpenChange={(val) => {
          setOpen(val);
          if (!val) resetForm();
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>{editingContent ? 'Edytuj treść' : 'Wgraj treść'}</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingContent ? 'Zaktualizuj informacje o treści' : 'Dodaj obraz lub wideo do biblioteki treści'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Tytuł</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label htmlFor="type">Typ treści</Label>
                <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="image">Obraz - pojedynczy ekran</SelectItem>
                    <SelectItem value="video">Wideo - pojedynczy ekran</SelectItem>
                    <SelectItem value="ad_combined">Reklama łączona - grupa ekranów</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-zinc-500 mt-2 space-y-1">
                  <p>
                    <span className="font-medium">Pojedynczy ekran:</span> Treść dla jednego wyświetlacza (rozdzielczość według konfiguracji ekranu)
                  </p>
                  <p>
                    <span className="font-medium">Grupa ekranów:</span> Obraz lub wideo wyświetlone jako overlay na całej grupie połączonych ekranów
                  </p>
                  {formData.type === 'ad_combined' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 mt-2">
                      <p className="text-blue-400 font-medium mb-1">💡 Sugerowane rozdzielczości:</p>
                      <div className="text-blue-300 text-xs space-y-2">
                        <div>
                          <p className="font-medium text-blue-400">Full HD (1080x1920 portrait):</p>
                          <ul className="space-y-0.5 ml-4">
                            <li>• 2 ekrany: 2160x1920px</li>
                            <li>• 3 ekrany: 3240x1920px</li>
                            <li>• 4 ekrany: 4320x1920px</li>
                            <li>• 2x2 grid: 2160x3840px</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-blue-400">4K UHD (2160x3840 portrait):</p>
                          <ul className="space-y-0.5 ml-4">
                            <li>• 2 ekrany: 4320x3840px</li>
                            <li>• 3 ekrany: 6480x3840px</li>
                            <li>• 4 ekrany: 8640x3840px</li>
                            <li>• 2x2 grid: 4320x7680px</li>
                          </ul>
                        </div>
                        <p className="text-blue-400">💡 Lub wideo (mp4/webm) - automatyczne dopasowanie do grupy</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {!editingContent && (
                <div>
                  <Label htmlFor="file">Plik</Label>
                  <Input
                    id="file"
                    type="file"
                    accept={
                      formData.type === 'video' || formData.type === 'ad_combined' 
                        ? 'video/mp4,video/webm,image/png,image/jpeg,image/jpg' 
                        : 'image/png,image/jpeg,image/jpg'
                    }
                    onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
                    disabled={uploading}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  {uploading && <p className="text-sm text-zinc-400 mt-1">Wysyłanie...</p>}
                  {formData.file_url && <p className="text-sm text-green-400 mt-1">✓ Plik wysłany</p>}
                </div>
              )}
              <div>
                <Label htmlFor="duration" className="flex items-center gap-2">
                  Domyślny czas trwania (sekundy)
                  <span className="text-xs text-zinc-500">(może być zmieniony w harmonogramie)</span>
                </Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                  required
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={!editingContent && (uploading || !formData.file_url)}
              >
                {editingContent ? 'Zaktualizuj treść' : 'Dodaj treść'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="all">Wszystkie</TabsTrigger>
            <TabsTrigger value="menu">Menu</TabsTrigger>
            <TabsTrigger value="ads">Reklamy</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contents.map((content) => (
                <Card key={content.id} data-testid={`content-card-${content.id}`} className="content-card bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-white text-lg">{content.title}</CardTitle>
                        <CardDescription className="text-zinc-400 mt-1">
                          {getContentTypeLabel(content.type)}
                        </CardDescription>
                      </div>
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        {content.type === 'video' ? (
                          <Video className="w-5 h-5 text-blue-400" />
                        ) : (
                          <FileImage className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {content.file_url && (
                      <div className="mb-4 rounded-lg overflow-hidden bg-zinc-800">
                        {content.type === 'video' ? (
                          <video src={process.env.REACT_APP_BACKEND_URL + content.file_url} className="w-full h-32 object-cover" />
                        ) : (
                          <img src={process.env.REACT_APP_BACKEND_URL + content.file_url} alt={content.title} className="w-full h-32 object-cover" />
                        )}
                      </div>
                    )}
                    <div className="space-y-1 text-sm text-zinc-400 mb-4">
                      <p>Czas trwania: {content.duration}s</p>
                      <p className="text-xs text-zinc-500 font-mono">ID: {content.id}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => openEdit(content)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edytuj
                      </Button>
                      <Button
                        onClick={() => handleDelete(content.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Usuń
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="menu">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contents.filter(c => c.type === 'menu').map((content) => (
                <Card key={content.id} className="content-card bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">{content.title}</CardTitle>
                    <CardDescription className="text-zinc-400">Menu</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-400 mb-4">Czas trwania: {content.duration}s</p>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => openEdit(content)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edytuj
                      </Button>
                      <Button
                        onClick={() => handleDelete(content.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Usuń
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="ads">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contents.filter(c => c.type.includes('ad')).map((content) => (
                <Card key={content.id} className="content-card bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">{content.title}</CardTitle>
                    <CardDescription className="text-zinc-400">{getContentTypeLabel(content.type)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {content.file_url && (
                      <img src={process.env.REACT_APP_BACKEND_URL + content.file_url} alt={content.title} className="w-full h-32 object-cover rounded mb-4" />
                    )}
                    <p className="text-sm text-zinc-400 mb-4">Czas trwania: {content.duration}s</p>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => openEdit(content)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edytuj
                      </Button>
                      <Button
                        onClick={() => handleDelete(content.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Usuń
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="media">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contents.filter(c => c.type === 'image' || c.type === 'video').map((content) => (
                <Card key={content.id} className="content-card bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">{content.title}</CardTitle>
                    <CardDescription className="text-zinc-400">{getContentTypeLabel(content.type)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {content.file_url && (
                      content.type === 'video' ? (
                        <video src={process.env.REACT_APP_BACKEND_URL + content.file_url} className="w-full h-32 object-cover rounded mb-4" />
                      ) : (
                        <img src={process.env.REACT_APP_BACKEND_URL + content.file_url} alt={content.title} className="w-full h-32 object-cover rounded mb-4" />
                      )
                    )}
                    <p className="text-sm text-zinc-400 mb-4">Czas trwania: {content.duration}s</p>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => openEdit(content)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 hover:bg-zinc-800"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edytuj
                      </Button>
                      <Button
                        onClick={() => handleDelete(content.id)}
                        variant="outline"
                        size="sm"
                        className="border-red-900 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Usuń
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {contents.length === 0 && (
          <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
            <CardContent className="py-12 text-center">
              <p className="text-zinc-500 mb-4">Brak treści w bibliotece</p>
              <p className="text-zinc-600 text-sm">Kliknij "Wgraj treść" lub "Stwórz menu" aby rozpocząć</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}